import { useState, useCallback } from 'react';
import { parse } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const DATE_REGEX = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

type DateOfBirthInputProps = {
    value: Date | null;
    onChange: (date: Date | null) => void;
    onError: (message: string) => void;
    onClearError: () => void;
    inputClassName?: string;
    buttonClassName?: string;
    placeholder?: string;
} & Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'placeholder' | 'onError'>;

export const DateOfBirthInput = ({ value, onChange, onError, onClearError, inputClassName, buttonClassName, placeholder = 'MM/DD/YYYY', ...rest }: DateOfBirthInputProps) => {
    const [inputValue, setInputValue] = useState<string>(
        value ? value.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '',
    );

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/\D/g, '');

        if (val.length > 2 && val.length <= 4) {
            val = `${val.slice(0, 2)}/${val.slice(2)}`;
        }
        else if (val.length > 4) {
            val = `${val.slice(0, 2)}/${val.slice(2, 4)}/${val.slice(4, 8)}`;
        }

        setInputValue(val);

        if (DATE_REGEX.test(val)) {
            const date = parse(val, 'MM/dd/yyyy', new Date());
            onClearError();
            onChange(date);
        }
        else {
            onChange(null);
        }
    }, [onChange, onClearError]);

    const handleBlur = useCallback(() => {
        if (inputValue.length > 0 && !DATE_REGEX.test(inputValue)) {
            onError('Date of birth must be valid (MM/DD/YYYY)');
        }
    }, [inputValue, onError]);

    const handleSelect = useCallback((date: Date) => {
        onClearError();
        onChange(date);
        setInputValue(date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }));
    }, [onChange, onClearError]);

    return (
        <div className="relative">
            <Input
                {...rest}
                className={inputClassName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={placeholder}
                type="text"
                value={inputValue}
                data-heap-redact-attributes="value"
            />
            <Popover>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        aria-label="Choose date of birth"
                        className={cn('absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0', buttonClassName)}
                    >
                        <CalendarIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 light" align="center" side="top">
                    <div className="light bg-background">
                        <Calendar
                            mode="single"
                            onSelect={handleSelect}
                            captionLayout="dropdown"
                            selected={value ?? undefined}
                            required={true}
                            className="light"
                            fixedWeeks
                        />
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};
